import { z } from 'zod';

/**
 * Module B · Insumos catalog + budget item composition lines.
 *
 * Insumos are the elemental inputs (a saco of cimento, an HH of
 * pedreiro, an hour of compactador). Compositions link a budget_item
 * to a list of insumos with quantity + snapshot price.
 */

export const insumoTypeSchema = z.enum(['material', 'mao_obra', 'equipamento']);
export type InsumoType = z.infer<typeof insumoTypeSchema>;

export const insumoSchema = z.object({
  id: z.string(),
  code: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  type: insumoTypeSchema,
  unitPriceCents: z.number().int().nonnegative(),
  sinapiCode: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Insumo = z.infer<typeof insumoSchema>;

export const createInsumoRequestSchema = z.object({
  code: z.string().max(30).nullable().optional(),
  description: z.string().trim().min(2).max(200),
  unit: z.string().trim().min(1).max(20),
  type: insumoTypeSchema,
  unitPriceCents: z.number().int().nonnegative(),
  sinapiCode: z.string().nullable().optional(),
});
export type CreateInsumoRequest = z.infer<typeof createInsumoRequestSchema>;

export const updateInsumoRequestSchema = createInsumoRequestSchema.partial();
export type UpdateInsumoRequest = z.infer<typeof updateInsumoRequestSchema>;

export const compositionLineSchema = z.object({
  id: z.string(),
  budgetItemId: z.string(),
  insumo: insumoSchema,
  quantity: z.number().positive(),
  unitPriceCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type CompositionLine = z.infer<typeof compositionLineSchema>;

export const upsertCompositionLineRequestSchema = z.object({
  insumoId: z.string().min(1),
  quantity: z.number().positive(),
  unitPriceCents: z.number().int().nonnegative(),
});
export type UpsertCompositionLineRequest = z.infer<typeof upsertCompositionLineRequestSchema>;

export const budgetItemCompositionSummarySchema = z.object({
  budgetItemId: z.string(),
  description: z.string(),
  plannedCents: z.number().int(),
  compositionTotalCents: z.number().int(),
  coveragePct: z.number(),
  lines: z.array(compositionLineSchema),
  byType: z.object({
    material: z.number().int(),
    mao_obra: z.number().int(),
    equipamento: z.number().int(),
  }),
});
export type BudgetItemCompositionSummary = z.infer<typeof budgetItemCompositionSummarySchema>;
